import type { PushPlatform } from './pushDraft.ts';
import { graphemeCount, guidanceSurfaceOf, lengthLimits, type PreviewSurface } from './pushLength.ts';

export interface LengthCounterProps {
  text: string;
  /** Platforms whose surfaces the text is measured against; none — only the count (titles have no guidance). */
  platforms?: readonly PushPlatform[];
  /** An image takes room on iOS: the limits get shorter. */
  withImage?: boolean;
  /** The notification state each platform's preview shows: its limit is called out. */
  selected?: Partial<Record<PushPlatform, PreviewSurface>>;
  /** Accessible name, e.g. «Message length». */
  label?: string;
}

/**
 * Characters of a push text as the device counts them — graphemes, so an emoji with modifiers is one — and where it
 * may be cut: the recommended limit of the surface the preview shows, then every surface of the platforms
 * (`PUSH_LENGTH_GUIDANCE`; recommendations, not limits — devices differ).
 */
export function LengthCounter({ text, platforms = [], withImage = false, selected = {}, label = 'Length' }: LengthCounterProps) {
  const count = graphemeCount(text);
  // Nothing typed (an empty override): only the count.
  const limits = count ? lengthLimits(count, platforms, withImage) : [];
  const focus = platforms.flatMap((p) => {
    const surface = selected[p];
    if (!surface) return [];
    const row = limits.find((l) => l.platform === p && l.surface === guidanceSurfaceOf(p, surface));
    return row ? [row] : [];
  });
  return (
    <div className="push-length" role="group" aria-label={label}>
      <span className="push-length__count">
        {count} {count === 1 ? 'character' : 'characters'}
      </span>
      {focus.map((l) =>
        l.exceeded ? (
          <p key={`${l.platform}/${l.surface}`} className="push-length__focus push-issue--warning">
            {l.label} shows about {l.limit}
            {withImage ? ' with an image' : ''}: {count - l.limit} over, the end may be cut.
          </p>
        ) : (
          <p key={`${l.platform}/${l.surface}`} className="push-length__focus hint">
            Fits the {l.label} (about {l.limit}
            {withImage ? ' with an image' : ''}).
          </p>
        ),
      )}
      {limits.length > 0 && (
        <ul className="push-length__limits" aria-label={`${label}: recommended limits`}>
          {limits.map((l) => {
            const current = focus.includes(l);
            return (
              <li key={`${l.platform}/${l.surface}`} aria-current={current || undefined} className={l.exceeded ? 'push-issue--warning' : undefined}>
                {l.label}: about {l.limit}
                {l.exceeded ? ' — may be cut' : ''}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
